import { GROUPING_MODE_OFF, GROUPING_MODE_SUM } from '../constants/actionTypes';
import { getGroupKey } from '../actions/grouping';

export const roundValue = value => Math.round(value * 1000) / 1000;
export const dateToUtc = date =>
  Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

const newDate = ts => new Date(ts * 1000);
const toUtcDate = ts => dateToUtc(newDate(ts));

const samplesToArray = samples =>
  samples && Object.keys(samples).length ? Object.values(samples) : [];

const getMostFrequentValue = arr =>
  arr
    .sort(
      (x, y) =>
        arr.filter(e => e === x).length - arr.filter(e => e === y).length
    )
    .pop();
const determineSampleInterval = samples => {
  if (!samples) {
    return null;
  }

  if (samples.length === 0) {
    return null;
  }

  const timestamps = Object.values(samples).map(sample => sample.timestamp);
  const deltas = timestamps.map(
    (timestamp, index) =>
      index < timestamps.length - 1
        ? timestamps[index + 1] - timestamps[index]
        : 0
  );
  return getMostFrequentValue(deltas);
};

export const filterSensorsByLoggerId = ({ sensors, loggerId }) =>
  Object.keys(sensors)
    .filter(sensorId => sensors[sensorId].data_logger === loggerId)
    .reduce((obj, key) => {
      obj[key] = sensors[key];
      return obj;
    }, {});

const getTimeSeries = ({
  mode,
  name,
  activeEnergyTypes,
  sensors,
  names,
  sumMode,
  type
}) => {
  return {
    mode: mode, // modes, off, sum, group
    name: name,
    multi: activeEnergyTypes.length > 1,
    max: {
      energy: 0,
      power: 0
    },
    sum: {
      energy: 0
    },
    groups: {},
    options: {
      lineWidth: 1
    },
    data: activeEnergyTypes.map(type => []), // array of arrays for xy values
    dataGroup:
      sensors && Object.keys(sensors).length > 0
        ? Object.keys(sensors).reduce((o, k) => {
            o[k] = activeEnergyTypes.map(type => []);
            return o;
          }, {})
        : undefined,
    dataSum: activeEnergyTypes.map(type => []),
    sensors,
    names,
    sumMode,
    type
  };
};

export const timeSeriesGroupingOff = ({ sensors, chartOptions, groupFilter }) =>
  Object.keys(sensors).reduce((obj, sensorId) => {
    obj[sensorId] = getTimeSeries({
      mode: groupFilter.mode,
      name: sensors[sensorId].description || sensors[sensorId].id,
      lineWidth: 1,
      multi: true,
      activeEnergyTypes: chartOptions.activeEnergyTypes,
      names: chartOptions.activeEnergyTypes.map(
        type => chartOptions.energyTypes[type].name
      ),
      type: sensors[sensorId].type
    });
    return obj;
  }, {});

export const timeSeriesGrouping = ({
  sensors,
  chartOptions,
  groupFilter,
  group,
  sumMode
}) =>
  Object.keys(group).reduce((obj, groupName) => {
    obj[groupName] = getTimeSeries({
      mode: groupFilter.mode,
      name: groupName,
      lineWidth: 1,
      multi: true,
      sumMode,
      activeEnergyTypes: chartOptions.activeEnergyTypes,
      sensors: group[groupName].sensors.reduce((obj, k) => {
        obj[k] = sensors[k];
        return obj;
      }, {}),
      names: group[groupName].sensors.map(
        sensorId =>
          sensors[sensorId] ? sensors[sensorId].description : sensorId
      )
    });
    return obj;
  }, {});

export const timeSeriesSum = ({
  sensors,
  chartOptions,
  groupFilter,
  group,
  sumMode
}) =>
  Object.keys(group).reduce((obj, groupName) => {
    obj[groupName] = getTimeSeries({
      mode: groupFilter.mode,
      name: groupName,
      lineWidth: 1,
      multi: true,
      sumMode,
      activeEnergyTypes: chartOptions.activeEnergyTypes,
      sensors: group[groupName].sensors.reduce((obj, k) => {
        obj[k] = sensors[k];
        return obj;
      }, {}),
      names: chartOptions.activeEnergyTypes.map(
        type => chartOptions.energyTypes[type].name
      )
    });
    return obj;
  }, {});

export const DataToChart = ({
  chartOptions,
  groupFilterRelations,
  groupFilter,
  sensors,
  samples,
  sampleInterval,
  filter
}) => {
  // todo: refactor
  // const sumSource = aggregationLevelSeconds <= 10 ? 'power' : 'energy'; // todo: check sum calculation on aggregationLevel = none
  // todo: check empty response

  if (sampleInterval === null) {
    const s = determineSampleInterval(samples);
    sampleInterval = s > 0 ? s : 2;
  }

  let timeSeries = {};
  const dataKeys = chartOptions.activeEnergyTypes;

  // off, show all sensors in it's own chart
  if (groupFilter.mode === GROUPING_MODE_OFF && !filter) {
    timeSeries = timeSeriesGroupingOff({ sensors, chartOptions, groupFilter });

    const samplesArray = samplesToArray(samples);

    // loop samples and allocate it to it's group
    for (let i = 0; i < samplesArray.length; i++) {
      const element = samplesArray[i];
      const nextIndex = samplesArray.length > i + 1 ? i + 1 : null;

      for (let [dataKeyIndex, dataKey] of dataKeys.entries()) {
        if (element.hasOwnProperty(dataKey)) {
          for (let [sensorId, value] of Object.entries(element[dataKey])) {
            if (dataKeyIndex === 0) {
              timeSeries[sensorId].sum.energy += element.energy[sensorId];
            }

            const roundedValue = roundValue(value);

            if (timeSeries[sensorId].max.power < roundedValue) {
              timeSeries[sensorId].max.power = roundedValue;
            }

            timeSeries[sensorId].data[dataKeyIndex].push({
              x: toUtcDate(element.timestamp),
              y: roundedValue
            });
          }
        }

        // fill gaps
        if (!nextIndex) {
          continue;
        }

        if (samplesArray[i].dataLogger !== samplesArray[nextIndex].dataLogger) {
          continue;
        }

        const delta =
          samplesArray[nextIndex].timestamp - samplesArray[i].timestamp;
        if (sampleInterval && delta && delta !== sampleInterval && delta > 0) {
          for (let n = sampleInterval; n < delta; n += sampleInterval) {
            for (let [sensorId] of Object.entries(element[dataKey])) {
              timeSeries[sensorId].data[dataKeyIndex].push({
                x: toUtcDate(element.timestamp + n),
                y: null,
                inserted: true
              });
            }
          }
        }
      }
    }

    return timeSeries;
  }

  const sum = groupFilter.mode === GROUPING_MODE_SUM;

  const sumMode = sum;
  const groupKey =
    filter && filter.filterType
      ? filter.filterType
      : getGroupKey(groupFilter.filter);
  const samplesArray = samplesToArray(samples);

  if (sum) {
    timeSeries = timeSeriesSum({
      sensors,
      chartOptions,
      groupFilter,
      group: groupFilterRelations[groupKey],
      sumMode
    });
  } else {
    const group =
      filter && filter.filter
        ? { [filter.filter]: groupFilterRelations[groupKey][filter.filter] }
        : groupFilterRelations[groupKey];

    timeSeries = timeSeriesGrouping({
      sensors,
      chartOptions,
      groupFilter,
      group
    });
  }

  for (let i = 0; i < samplesArray.length; i++) {
    const element = samplesArray[i];
    const nextIndex = samplesArray.length > i + 1 ? i + 1 : null;

    for (let [dataKeyIndex, dataKey] of dataKeys.entries()) {
      if (element.hasOwnProperty(dataKey)) {
        const timestamp = toUtcDate(element.timestamp);

        for (let [sensorId, value] of Object.entries(element[dataKey])) {
          const grp = sensors[sensorId][groupKey];

          if (dataKeyIndex === 0) {
            timeSeries[grp].sum.energy += element.energy[sensorId];
          }

          const roundedValue = roundValue(value);

          if (timeSeries[grp].max.power < roundedValue) {
            timeSeries[grp].max.power = roundedValue;
          }

          if (sum) {
            const timeStampString = timestamp.toString();
            timeSeries[grp].dataSum[dataKeyIndex].hasOwnProperty(timestamp)
              ? timeSeries[grp].dataSum[dataKeyIndex][timeStampString].push(
                  value
                )
              : (timeSeries[grp].dataSum[dataKeyIndex][timeStampString] = [
                  value
                ]);
          }

          timeSeries[grp].dataGroup[sensorId][dataKeyIndex].push({
            x: timestamp,
            y: roundedValue
          });
        }

        // fill gaps
        if (!nextIndex) {
          continue;
        }

        if (samplesArray[i].dataLogger !== samplesArray[nextIndex].dataLogger) {
          continue;
        }

        const delta =
          samplesArray[nextIndex].timestamp - samplesArray[i].timestamp;
        if (sampleInterval && delta && delta !== sampleInterval && delta > 0) {
          for (let n = sampleInterval; n < delta; n += sampleInterval) {
            for (let [sensorId] of Object.entries(element[dataKey])) {
              const grp = sensors[sensorId][groupKey];

              if (sum) {
                const timeStampString = (
                  (element.timestamp + n) *
                  1000
                ).toString();
                timeSeries[grp].dataSum[dataKeyIndex].hasOwnProperty(
                  timeStampString
                )
                  ? timeSeries[grp].dataSum[dataKeyIndex][timeStampString].push(
                      null
                    )
                  : (timeSeries[grp].dataSum[dataKeyIndex][timeStampString] = [
                      null
                    ]);
              }

              timeSeries[grp].dataGroup[sensorId][dataKeyIndex].push({
                x: toUtcDate(element.timestamp + n),
                y: null,
                inserted: true
              });
            }
          }
        }
      }
    }
  }

  if (sum) {
    for (let timeSeriesObject of Object.values(timeSeries)) {
      for (let [dataKeyIndex, dataKey] of dataKeys.entries()) {
        timeSeriesObject.data[dataKeyIndex] = Object.keys(
          timeSeriesObject.dataSum[dataKeyIndex]
        )
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .map(timestampString => {
            const item =
              timeSeriesObject.dataSum[dataKeyIndex][timestampString];
            let roundedValue = roundValue(item.reduce((a, b) => a + b, 0));

            if (timeSeriesObject.max.power < roundedValue) {
              timeSeriesObject.max.power = roundedValue;
            }

            if (dataKey === 'power_factor' && item.length > 0) {
              roundedValue = roundedValue / item.length;
            }

            return {
              x: parseInt(timestampString, 10),
              y: roundedValue
            };
          });
      }
    }
  }

  return timeSeries;
};

export const sortByNameAndType = (a, b) => {
  // sort first by type, main > ct
  if (a.type === b.type) {
    // then name, alphabetical
    return a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0;
  }

  return a.type > b.type ? -1 : 1;
};



// WEBPACK FOOTER //
// ./src/helper/DataToChart.js